import { useEffect, useState } from "react";
import { Alert, Button, Field } from "../../../ui";
import { PageHeader } from "../../workspace/shared";
import { date as dateTime, money } from "../../workspace/model";
import { parseDashboardDate, currentSeoulDate } from "../schema";
import type { HistoricalMetrics, LiveMetrics } from "../service-status-api";
import "./ServiceStatusView.css";

export interface ServiceStatusViewProps {
  selectedDate: string; latestDate: string;
  history?: HistoricalMetrics; live?: LiveMetrics;
  historyLoading?: boolean; liveLoading?: boolean;
  historyError?: string; liveError?: string;
  onQuery: (date: string) => void; onRefresh: () => void;
}

export function ServiceStatusView({ selectedDate, latestDate, history, live, historyLoading, liveLoading, historyError, liveError, onQuery, onRefresh }: ServiceStatusViewProps) {
  const [inputDate, setInputDate] = useState(selectedDate);
  const [dateError, setDateError] = useState<string>();
  useEffect(() => { setInputDate(selectedDate); setDateError(undefined); }, [selectedDate]);
  return <>
    <PageHeader id="ADM-S04" title="서비스 현황" description="날짜별 이용 현황과 현재 처리할 업무를 확인합니다." />
    <section className="service-status" aria-labelledby="service-history-title">
      <h2 id="service-history-title">날짜별 현황</h2>
      <form className="admin-dashboard-review__date" noValidate onSubmit={(event) => {
        event.preventDefault();
        if (!parseDashboardDate(inputDate) || inputDate > latestDate || inputDate >= currentSeoulDate()) {
          setDateError("어제까지의 날짜를 선택해 주세요."); return;
        }
        setDateError(undefined); onQuery(inputDate);
      }}>
        <Field label="기준일" type="date" max={latestDate} value={inputDate} error={dateError} onChange={(event) => setInputDate(event.currentTarget.value)} />
        <Button size="small" variant="secondary" type="submit" disabled={historyLoading}>조회</Button>
      </form>
      {historyLoading ? <p role="status">선택한 날짜의 현황을 조회하고 있습니다.</p> : historyError ? <Alert persistent variant="danger" title="날짜별 현황을 불러오지 못했습니다">{historyError}</Alert> : history ? <>
        <p>{history.date} 기준</p>
        <dl className="service-status__grid">
          {[["누적 회원", history.cumulativeMembers], ["신규 가입", history.joinedMembers], ["해지", history.closedMembers], ["순증", history.netMembers]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{Number(value).toLocaleString("ko-KR")}명</dd></div>)}
        </dl>
        <dl className="service-status__grid service-status__grid--money">
          {[["월정액 결제", history.payments], ["월정액 취소", history.cancellations], ["계좌 환불 완료", history.bankRefunds]].map(([label, metric]) => {
            const value = metric as HistoricalMetrics["payments"];
            return <div key={label as string}><dt>{label as string}</dt><dd>{value.count.toLocaleString("ko-KR")}건 <span>{money(value.amount)}</span></dd></div>;
          })}
        </dl>
      </> : null}
    </section>
    <section className="service-status" aria-labelledby="service-live-title" aria-busy={liveLoading}>
      <div className="service-status__heading"><h2 id="service-live-title">현재 업무 현황</h2><Button size="small" variant="secondary" disabled={liveLoading} onClick={onRefresh}>새로고침</Button></div>
      {liveLoading ? <p role="status">처리 대기 현황과 모계좌 잔액을 갱신하고 있습니다.</p> : liveError ? <Alert persistent variant="danger" title="현재 업무 현황을 불러오지 못했습니다">{liveError}</Alert> : live ? <>
        <p>갱신 시각 {dateTime(live.refreshedAt)}</p>
        <dl className="service-status__grid">
          <div><dt>거래 심사 대기</dt><dd>{live.work.transactionReviews.toLocaleString("ko-KR")}건</dd></div>
          <div><dt>환불 요청</dt><dd>{live.work.requestedRefunds.count.toLocaleString("ko-KR")}건 <span>{money(live.work.requestedRefunds.amount)}</span></dd></div>
          <div><dt>혜택지원 심사 대기</dt><dd>{live.work.benefitReviews.toLocaleString("ko-KR")}건</dd></div>
          <div><dt>문의 접수</dt><dd>{live.work.receivedInquiries.toLocaleString("ko-KR")}건</dd></div>
        </dl>
        <dl className="service-status__grid service-status__grid--balances">
          {live.balances.map((balance) => <div key={balance.purpose}>
            <dt>{balance.purpose === "CUSTOMER_REFUND" ? "환불 모계좌" : "송금 모계좌"} 잔액</dt>
            <dd>{balance.balanceAmount == null ? "조회 불가" : money(balance.balanceAmount)}</dd>
            <p>{balance.bankName} · 끝 {balance.virtualAccountNumber.slice(-3)}</p>
            {balance.capturedAt ? <p>잔액 조회 {dateTime(balance.capturedAt)}</p> : null}
            {balance.status !== "CURRENT" ? <p role="status">{balance.message ?? "잔액을 조회하지 못했습니다."}</p> : null}
          </div>)}
        </dl>
      </> : null}
    </section>
  </>;
}
